<script setup lang="ts">
import { trackResponsibleIconSectionControl } from '~/tracking/responsibleGamingEvents.js'

defineOptions({
	name: 'ControlSection',
})

const { t: $t } = useI18n()

const sendEventIcon = (index: number, open: boolean) => {
	if (open) return
	trackResponsibleIconSectionControl({ icon_id: index + 1 })
}

const controlQuestions = [
	{
		label: $t('Have you ever spent more money or time playing than you intended?'),
		content: $t(
			`Regularly exceeding your limits is a key warning sign. To stay in control, it's essential to set firm budgets and time restrictions before you begin playing and stick to them`,
		),
	},
	{
		label: $t('Have you ever lied to friends or family about how much you play?'),
		content: $t(
			`Hiding your gaming habits is a strong indicator of a problem. Secrecy often means that, on some level, you already suspect that something is wrong, and it serves as a barrier to seeking support`,
		),
	},
	{
		label: $t('Has playing caused you stress, anxiety, or financial problems?'),
		content: $t(
			`Gaming should be fun. When it becomes a source of financial strain or negative emotions like stress and anxiety, it has stopped being entertainment and is a clear signal to take a step back`,
		),
	},
	{
		label: $t('Do you find yourself constantly thinking about playing?'),
		content: $t(
			`If gaming is always on your mind, it might become a dependency. A healthy relationship with gaming means it's just one of many activities you enjoy, not your primary mental focus`,
		),
	},
]
</script>

<template>
	<section class="bg-primary-900 py-10 sm:py-20">
		<div class="container space-y-20">
			<div class="text-center text-neutral-50 space-y-4 max-w-3xl mx-auto">
				<Icon
					name="ph:warning"
					class="size-8 text-primary-500"
				/>
				<h2 class="text-3xl sm:text-5xl font-semibold">{{ $t(`Are You in Control`) }}?</h2>
				<p class="text-[18px] font-normal">
					{{
						$t(
							`Ask yourself these questions honestly. If you answer 'yes' to several of them, it might be time to re-evaluate your playing habits`,
						)
					}}:
				</p>
			</div>

			<div class="max-w-[1012px] mx-auto space-y-9">
				<UAccordion
					:items="controlQuestions"
					:ui="{
						wrapper: 'flex flex-col w-full gap-6 divide-y divide-[#DCDDEB] border-b border-[#DCDDEB] pb-6',
					}"
				>
					<template #default="{ item, index, open }">
						<UButton
							color="white"
							variant="ghost"
							:ui="{ rounded: 'rounded-none', padding: { sm: 'px-1 sm:px-3 sm:py-3' } }"
							class="hover:bg-white/[0.1] flex justify-between items-center gap-10 sm:gap-5 rounded sm:rounded-xl"
							:class="{ 'mt-6': index > 0 }"
							@click="sendEventIcon(index, open)"
						>
							<h3 class="text-neutral-50 text-base sm:text-xl font-medium sm:font-semibold text-left">
								{{ item.label }}
							</h3>

							<template #trailing>
								<div class="rounded-full bg-neutral-50 p-2 size-6 flex justify-center items-center">
									<UIcon
										:name="open ? 'i-heroicons-x-mark-20-solid' : 'i-material-symbols:add-2-rounded'"
										class="shrink-0 size-4 text-success-500 ms-auto transform transition-transform duration-200"
									/>
								</div>
							</template>
						</UButton>
					</template>
					<template #item="{ item }">
						<p class="text-neutral-50 text-sm sm:text-base px-1 sm:px-3">{{ item.content }}.</p>
					</template>
				</UAccordion>
			</div>
		</div>
	</section>
</template>
